/**
 * Map Module Routes Configuration
 *
 * This file contains all route definitions for the Map utility module.
 * Routes are organized by feature for better maintainability.
 */

import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import Layout from '../../../../components/Layout';

// Main Map Page
const MapPage = lazy(() => import('../index'));

// Map Sub-Pages
const MapMasters = lazy(() => import('../pages/map-masters'));
const TileCacheManagement = lazy(() => import('../pages/tiles/tile-cache'));
const VectorMapsPage = lazy(() => import('../pages/vector-maps'));
const OSMBrightMapPage = lazy(() => import('../pages/osm-bright'));
const DsDdnMapPage = lazy(() => import('../pages/ds-ddn-map'));
const DsDdnRoadNetworkPage = lazy(() => import('../pages/ds-ddn-road-network'));
const GovernmentAssetsPage = lazy(() => import('../pages/government-assets'));
const RoadNetworkPage = lazy(() => import('../pages/road-network'));
const WaterNetworkPage = lazy(() => import('../pages/water-network'));
const SewerageNetworkPage = lazy(() => import('../pages/sewerage-network'));
const GreeningBeautificationPage = lazy(() => import('../pages/greening-beautification'));
const PropertyTaxPage = lazy(() => import('../pages/property-tax'));
const ReSurveyPage = lazy(() => import('../pages/resurvey'));
const DigitalDoorNoPage = lazy(() => import('../pages/digital-door-no'));
const DdnPaymentsPage = lazy(() => import('../pages/ddn-payments'));
const TradeLicensesPage = lazy(() => import('../pages/trade-licenses'));
const StreetVendorsPage = lazy(() => import('../pages/street-vendors'));
const OperatoinalMapDashboard = lazy(() => import('../pages/dashboard'));
const StateMapDashboard = lazy(() => import('../pages/dashboard/state-dashboard'));
const AdministrateMapBoundaries = lazy(() => import('../pages/administrate-map-boundaries'));
// Digital Twin - removed (module not included in clean build)
const DigitalTwin3DMap = lazy(() => Promise.resolve({ default: () => null }));

// Map child routes (for /app/gis-map/* routes)
export const mapChildRoutes: RouteObject[] = [
  // GIS Map Viewer (main map page) - /app/gis-map
  {
    path: 'gis-map',
    element: <MapPage />,
    children: [
      {
        index: true,
        element: <MapPage />
      },
      {
        path: ':handler',
        element: <MapPage />
      }
    ]
  },
  {
    path: 'gis-map/:handler',
    element: <MapPage />
  },
  // Map Management Routes - /app/gis-map/*
  {
    path: 'gis-map/workspaces',
    element: <MapMasters />
  },
  {
    path: 'gis-map/tile-cache',
    element: <TileCacheManagement />
  },
  {
    path: 'gis-map/vector-maps',
    element: <VectorMapsPage />
  },
  {
    path: 'gis-map/integrated-map',
    element: <OSMBrightMapPage />
  },
  {
    path: 'gis-map/ds-ddn-map',
    element: <DsDdnMapPage />
  },
  {
    path: 'gis-map/gis-ddn-road-network',
    element: <DsDdnRoadNetworkPage />
  },
  {
    path: 'gis-map/layer-network/:handler/:mode',
    element: <DsDdnRoadNetworkPage />
  },
  {
    path: 'gis-map/integrated-map-operational-dashboard',
    element: <OperatoinalMapDashboard />
  },
  {
    path: 'gis-map/state-map-dashboard',
    element: <StateMapDashboard />
  },
  {
    path: 'gis-map/administrate-map-boundaries',
    element: <AdministrateMapBoundaries />
  },
  // GIS Feature Routes - /app/gis-map/*
  {
    path: 'gis-map/government-assets',
    element: <GovernmentAssetsPage />
  },
  {
    path: 'gis-map/road-network',
    element: <RoadNetworkPage />
  },
  {
    path: 'gis-map/water-network',
    element: <WaterNetworkPage />
  },
  {
    path: 'gis-map/sewerage-network',
    element: <SewerageNetworkPage />
  },
  {
    path: 'gis-map/greening-beautification',
    element: <GreeningBeautificationPage />
  },
  {
    path: 'gis-map/property-tax',
    element: <PropertyTaxPage />
  },
  {
    path: 'gis-map/resurvey',
    element: <ReSurveyPage />
  },
  {
    path: 'gis-map/digital-door-no',
    element: <DigitalDoorNoPage />
  },
  {
    path: 'gis-map/ddn-payments',
    element: <DdnPaymentsPage />
  },
  {
    path: 'gis-map/trade-licenses',
    element: <TradeLicensesPage />
  },
  {
    path: 'gis-map/street-vendors',
    element: <StreetVendorsPage />
  },
  // Digital Twin 3D Map - /app/gis-map/digital-twin
  {
    path: 'gis-map/digital-twin',
    element: <DigitalTwin3DMap />
  }
];

// Legacy public map routes - DEPRECATED, use /app/gis-map instead
// Kept for backwards compatibility, redirects to new route
export const publicMapRoutes: RouteObject = {
  path: '/map',
  element: <Layout />,
  children: [
    {
      index: true,
      element: <MapPage />
    },
    {
      path: ':handler',
      element: <MapPage />
    }
  ]
};

export default mapChildRoutes;
